import { ReactNode, useMemo } from 'react';

import { usePlatformTableRowByIdTeam } from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import Box from '@mui/material/Box';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { JSONBlob, getErrorMessage, DateTime, TagsDisplay, FilterOperator } from 'shared/base';

import { CLOUD_ASSET_NON_UNIQUE_KEY, CLOUD_ASSET_UNIQUE_KEY } from '../../../constants/data';
import { useTableStateContext } from '../../../hooks/useTableStateContext';
import { AssetInventoryGridColDef, AssetInventoryTableRow } from '../../../types';

import { tryParseJson } from '../../../utils/table';
import { conditionallyQuoteWrapTags, setKeyValuesOnString } from '../../../utils/tableState';

interface ResourceDetailsTableProps {
  column: string;
  filterConstructor?: (record: AssetInventoryTableRow) => [string, string?, FilterOperator?];
  formatValue?: (value: string) => ReactNode;
  label: string;
}

const staticProperties: ResourceDetailsTableProps[] = [
  { column: CLOUD_ASSET_NON_UNIQUE_KEY, label: 'CloudQuery ID' },
  { column: 'resource_type', label: 'Source Table' },
  {
    column: 'resource_type_label',
    filterConstructor: (record: AssetInventoryTableRow) => [
      'resource_type',
      `"${record?.['resource_type']}"`,
      FilterOperator.equal,
    ],
    label: 'Type',
  },
  { column: 'name', label: 'Name' },
  {
    column: 'region',
    filterConstructor: (record: AssetInventoryTableRow): [string, string?, FilterOperator?] => [
      'region',
      `"${record?.['region']}"`,
      FilterOperator.equal,
    ],
    label: 'Region',
  },
];

const getLastSyncTime = (value: string | undefined) => {
  if (!value) {
    return '-';
  }

  const dateValue = new Date(value);

  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    return <DateTime date={dateValue} type="since" />;
  }

  return '-';
};

export const ResourceDetails = ({
  columns,
  onClose,
  record,
}: {
  columns: AssetInventoryGridColDef[];
  onClose: () => void;
  record?: AssetInventoryTableRow;
}) => {
  const { currentMembership } = useBaseAuthenticated();
  const { palette } = useTheme<any>();
  const { filter, overwriteFilter } = useTableStateContext();

  const enabled = !!record?.[CLOUD_ASSET_UNIQUE_KEY] && !!record?.resource_type;

  const {
    data: rowResponse,
    error,
    isLoading,
  } = usePlatformTableRowByIdTeam(
    currentMembership.team.name,
    record?.resource_type ?? '',
    (record?.[CLOUD_ASSET_UNIQUE_KEY] as string) ?? '',
    { filter: [filter] },
    {
      query: {
        enabled,
      },
    },
  );

  // [PURPOSE]: Generate JSONBlob data, highlight and depth
  const { depth, highlight, parsedJsonData } = useMemo(() => {
    const highlight: Record<string, string> = {};
    let depth = 1;

    if (rowResponse?.data?.matches) {
      for (const match of rowResponse.data.matches) {
        const splitKey = match.key.split('.');
        depth = Math.max(depth, splitKey.length);

        const key = splitKey.pop() ?? '';
        highlight[key] = match.value;
      }
    }

    return {
      depth,
      highlight,
      parsedJsonData: tryParseJson(rowResponse?.data?.data),
    };
  }, [rowResponse]);

  const customColumnsProperties = columns
    .filter(({ isCustomColumn }) => isCustomColumn)
    .map(({ field, headerName }) => {
      const filterConstructor = (
        record: AssetInventoryTableRow,
      ): [string, string?, FilterOperator?] => [
        field,
        `"${record?.[field]}"`,
        FilterOperator.equal,
      ];

      return {
        column: field,
        filterConstructor,
        label: headerName,
      } as ResourceDetailsTableProps;
    });
  const allProperties = [...staticProperties, ...customColumnsProperties];

  return (
    <Stack gap={2} height="100%" overflow="hidden">
      <Card
        sx={{
          backgroundColor: palette.background.paperTertiary,
          flexBasis: 'auto',
          minHeight: 'fit-content',
        }}
      >
        <CardContent sx={{ height: '100%', overflowY: 'auto' }}>
          <Stack gap={2} height="100%">
            <Grid columnSpacing={2} container={true} height="100%" rowSpacing={1}>
              {allProperties.map(({ column, filterConstructor, formatValue, label }) => {
                const rawValue = record?.[column as keyof AssetInventoryTableRow] as string;
                const displayValue = rawValue || '-';

                return (
                  <Grid key={column} size={3}>
                    <Stack>
                      <Typography variant="overline">{label}</Typography>
                      {rawValue && filterConstructor ? (
                        <Box>
                          {
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <Link
                              onClick={() => {
                                if (record) {
                                  overwriteFilter(
                                    setKeyValuesOnString(filter, [filterConstructor(record)]),
                                  );
                                }
                                onClose();
                              }}
                              sx={{ cursor: 'pointer' }}
                              variant="body2"
                            >
                              {displayValue}
                            </Link>
                          }
                        </Box>
                      ) : (
                        <Typography maxHeight="120px" overflow="auto" variant="body2">
                          {formatValue ? formatValue(displayValue) : displayValue}
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                );
              })}
              <Grid key="last_sync" size={3}>
                <Stack>
                  <Typography variant="overline">Last synced</Typography>
                  <Typography maxHeight="120px" overflow="auto" variant="body2">
                    {getLastSyncTime(parsedJsonData._cq_sync_time)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid overflow="auto" size="grow">
                <Typography variant="overline">tags</Typography>
                <Typography variant="body2">
                  <TagsDisplay
                    limit={3}
                    nullValue="-"
                    onClick={(value: string) => {
                      const parsedValue = conditionallyQuoteWrapTags(value);
                      overwriteFilter(
                        setKeyValuesOnString(filter, [
                          ['tags', parsedValue, FilterOperator.contains],
                        ]),
                      );
                      onClose();
                    }}
                    tags={record?.tags}
                    wrap={true}
                  />
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
      <Box
        sx={{
          display: 'flex',
          minHeight: '200px',
        }}
      >
        <Card sx={{ overflow: 'auto', width: '100%' }}>
          <CardContent sx={{ backgroundColor: palette.background.paperTertiary }}>
            {isLoading && enabled ? (
              <CircularProgress />
            ) : (
              <JSONBlob
                errorMessage={getErrorMessage(error)}
                highlight={highlight}
                json={parsedJsonData}
                jsonOptions={{ collapsed: depth }}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
};
